"use client";

import React, { useEffect, useState } from 'react';
import Breadcrumb from './ui/Breadcrumb';
import { useTranslations } from 'next-intl';
import { CookieIcon, AssignmentIcon } from './icons';
import styles from './StaticPage.module.css';

interface Section {
    id: string;
    title: string;
}

export default function CookiePolicy() {
    const [activeSection, setActiveSection] = useState<string>('what-are-cookies');

    const tCommon = useTranslations('common');
    const t = useTranslations('cookiePolicy');

    const breadcrumbItems = [
        { label: tCommon('breadcrumb.home'), path: '/' },
        { label: tCommon('breadcrumb.cookiePolicy'), path: '/cookie-policy' }
    ];

    const sections: Section[] = Array.from({ length: 13 }, (_, i) => ({
        id: t(`sections.${i}.id`),
        title: t(`sections.${i}.title`),
    }));

    const scrollToSection = (sectionId: string) => {
        const element = document.getElementById(sectionId);
        if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
            setActiveSection(sectionId);
        }
    };

    useEffect(() => {
        const handleScroll = () => {
            const scrollPosition = window.scrollY + 150;
            for (const section of sections) {
                const element = document.getElementById(section.id);
                if (element) {
                    const { offsetTop, offsetHeight } = element;
                    if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
                        setActiveSection(section.id);
                        break;
                    }
                }
            }
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, [sections]);

    return (
        <div className={styles.pageWrapper}>
            <div className={styles.breadcrumbBar}>
                <div className={styles.container}><Breadcrumb items={breadcrumbItems} /></div>
            </div>

            <div className={styles.heroSection}>
                <div className={styles.heroInner}>
                    <h1 className={styles.heroTitle}>{t('hero.title')}</h1>
                    <p className={styles.heroSubtitle}>{t('hero.subtitle')}</p>
                    <p className={styles.bodyTextSm} style={{ fontWeight: 600, marginTop: 16 }} dangerouslySetInnerHTML={{ __html: t('hero.effectiveDate') }} />
                </div>
            </div>

            <div className={styles.container}>
                <div className={styles.legalLayout}>
                    <div>
                        <section id="what-are-cookies" className={styles.contentSection}>
                            <h2 className={styles.sectionTitle}>{t('content.whatAreCookies.title')}</h2>
                            <p className={styles.contentText}>{t('content.whatAreCookies.text')}</p>
                            <p className={styles.contentText}>{t('content.whatAreCookies.additionalText')}</p>
                        </section>

                        <section id="types-of-cookies" className={styles.contentSection}>
                            <h2 className={styles.sectionTitle}>{t('content.typesOfCookies.title')}</h2>
                            <p className={styles.contentText}>{t('content.typesOfCookies.text')}</p>
                            <ul className={styles.bulletList}>
                                <li dangerouslySetInnerHTML={{ __html: t.raw('content.typesOfCookies.items.0') }} />
                                <li dangerouslySetInnerHTML={{ __html: t.raw('content.typesOfCookies.items.1') }} />
                                <li dangerouslySetInnerHTML={{ __html: t.raw('content.typesOfCookies.items.2') }} />
                            </ul>
                        </section>

                        <section id="legal-basis" className={styles.contentSection}>
                            <h2 className={styles.sectionTitle}>{t('content.legalBasis.title')}</h2>
                            <p className={styles.contentText}>{t('content.legalBasis.text')}</p>
                            <ul className={styles.bulletList}>
                                <li dangerouslySetInnerHTML={{ __html: t.raw('content.legalBasis.items.0') }} />
                                <li dangerouslySetInnerHTML={{ __html: t.raw('content.legalBasis.items.1') }} />
                            </ul>
                            <div className={styles.infoBox}>
                                <p>{t('content.legalBasis.note')}</p>
                            </div>
                        </section>

                        <section id="essential-cookies" className={styles.contentSection}>
                            <h2 className={styles.sectionTitle}>{t('content.essentialCookies.title')}</h2>
                            <p className={styles.contentText}>{t('content.essentialCookies.text')}</p>
                            <ul className={styles.bulletList}>
                                {[0, 1, 2, 3].map((i) => (
                                    <li key={i}>{t(`content.essentialCookies.items.${i}`)}</li>
                                ))}
                            </ul>
                            <div className={styles.infoBox}>
                                <p>{t('content.essentialCookies.note')}</p>
                            </div>
                        </section>

                        <section id="analytics-cookies" className={styles.contentSection}>
                            <h2 className={styles.sectionTitle}>{t('content.analyticsCookies.title')}</h2>
                            <p className={styles.contentText}>{t('content.analyticsCookies.text')}</p>
                            <ul className={styles.bulletList}>
                                <li dangerouslySetInnerHTML={{ __html: t.raw('content.analyticsCookies.tools.0') }} />
                                <li dangerouslySetInnerHTML={{ __html: t.raw('content.analyticsCookies.tools.1') }} />
                                <li dangerouslySetInnerHTML={{ __html: t.raw('content.analyticsCookies.tools.2') }} />
                                <li dangerouslySetInnerHTML={{ __html: t.raw('content.analyticsCookies.tools.3') }} />
                            </ul>
                            <div className={styles.infoBox}>
                                <p dangerouslySetInnerHTML={{ __html: t.raw('content.analyticsCookies.note') }} />
                            </div>
                        </section>

                        <section id="advertising-cookies" className={styles.contentSection}>
                            <h2 className={styles.sectionTitle}>{t('content.advertisingCookies.title')}</h2>
                            <p className={styles.contentText}>{t('content.advertisingCookies.text')}</p>
                            <ul className={styles.bulletList}>
                                <li dangerouslySetInnerHTML={{ __html: t.raw('content.advertisingCookies.tools.0') }} />
                            </ul>
                            <p className={styles.contentText} dangerouslySetInnerHTML={{ __html: t.raw('content.advertisingCookies.tagManagement') }} />
                            <div className={styles.infoBox}>
                                <p dangerouslySetInnerHTML={{ __html: t.raw('content.advertisingCookies.note') }} />
                            </div>
                        </section>

                        <section id="consent-management" className={styles.contentSection}>
                            <h2 className={styles.sectionTitle}>{t('content.consentManagement.title')}</h2>
                            <p className={styles.contentText}>{t('content.consentManagement.text')}</p>
                            <ul className={styles.bulletList}>
                                <li dangerouslySetInnerHTML={{ __html: t.raw('content.consentManagement.items.0') }} />
                                <li dangerouslySetInnerHTML={{ __html: t.raw('content.consentManagement.items.1') }} />
                            </ul>
                            <p className={styles.contentText} dangerouslySetInnerHTML={{ __html: t.raw('content.consentManagement.consentMode') }} />
                            <ul className={styles.bulletList}>
                                <li dangerouslySetInnerHTML={{ __html: t.raw('content.consentManagement.consentSignals.0') }} />
                                <li dangerouslySetInnerHTML={{ __html: t.raw('content.consentManagement.consentSignals.1') }} />
                                <li dangerouslySetInnerHTML={{ __html: t.raw('content.consentManagement.consentSignals.2') }} />
                                <li dangerouslySetInnerHTML={{ __html: t.raw('content.consentManagement.consentSignals.3') }} />
                            </ul>
                            <div className={styles.infoBox}>
                                <p dangerouslySetInnerHTML={{ __html: t.raw('content.consentManagement.consentNote') }} />
                            </div>
                            <p className={styles.contentText} dangerouslySetInnerHTML={{ __html: t.raw('content.consentManagement.withdrawConsent') }} />
                        </section>

                        <section id="cookie-duration" className={styles.contentSection}>
                            <h2 className={styles.sectionTitle}>{t('content.cookieDuration.title')}</h2>
                            <p className={styles.contentText}>{t('content.cookieDuration.text')}</p>
                            <ul className={styles.bulletList}>
                                <li dangerouslySetInnerHTML={{ __html: t.raw('content.cookieDuration.items.0') }} />
                                <li dangerouslySetInnerHTML={{ __html: t.raw('content.cookieDuration.items.1') }} />
                            </ul>
                            <div className={styles.infoBox}>
                                <p>{t('content.cookieDuration.note')}</p>
                            </div>
                        </section>

                        <section id="third-party-cookies" className={styles.contentSection}>
                            <h2 className={styles.sectionTitle}>{t('content.thirdPartyCookies.title')}</h2>
                            <p className={styles.contentText}>{t('content.thirdPartyCookies.text')}</p>
                            <ul className={styles.bulletList}>
                                <li><a href={t('content.thirdPartyCookies.items.0.url')} target="_blank" rel="noopener" style={{ color: 'var(--color-primary)', textDecoration: 'none' }}>{t('content.thirdPartyCookies.items.0.name')}</a></li>
                                <li><a href={t('content.thirdPartyCookies.items.1.url')} target="_blank" rel="noopener" style={{ color: 'var(--color-primary)', textDecoration: 'none' }}>{t('content.thirdPartyCookies.items.1.name')}</a></li>
                                <li><a href={t('content.thirdPartyCookies.items.2.url')} target="_blank" rel="noopener" style={{ color: 'var(--color-primary)', textDecoration: 'none' }}>{t('content.thirdPartyCookies.items.2.name')}</a></li>
                                <li><a href={t('content.thirdPartyCookies.items.3.url')} target="_blank" rel="noopener" style={{ color: 'var(--color-primary)', textDecoration: 'none' }}>{t('content.thirdPartyCookies.items.3.name')}</a></li>
                                <li>
                                    <a href={t('content.thirdPartyCookies.items.4.url')} target="_blank" rel="noopener" style={{ color: 'var(--color-primary)', textDecoration: 'none' }}>{t('content.thirdPartyCookies.items.4.name')}</a>
                                    {' - '}{t('content.thirdPartyCookies.items.4.description')}
                                </li>
                            </ul>
                            <div className={styles.infoBox}>
                                <p>{t('content.thirdPartyCookies.note')}</p>
                            </div>
                        </section>

                        <section id="international-transfers" className={styles.contentSection}>
                            <h2 className={styles.sectionTitle}>{t('content.internationalTransfers.title')}</h2>
                            <p className={styles.contentText}>{t('content.internationalTransfers.text')}</p>
                            <p className={styles.contentText}>{t('content.internationalTransfers.safeguards')}</p>
                            <ul className={styles.bulletList}>
                                {[0, 1, 2].map((i) => (
                                    <li key={i}>{t(`content.internationalTransfers.items.${i}`)}</li>
                                ))}
                            </ul>
                            <div className={styles.infoBox}>
                                <p>{t('content.internationalTransfers.note')}</p>
                            </div>
                        </section>

                        <section id="managing-cookies" className={styles.contentSection}>
                            <h2 className={styles.sectionTitle}>{t('content.managingCookies.title')}</h2>
                            <p className={styles.contentText}>{t('content.managingCookies.text')}</p>
                            <ul className={styles.bulletList}>
                                <li dangerouslySetInnerHTML={{ __html: t.raw('content.managingCookies.items.0') }} />
                                <li dangerouslySetInnerHTML={{ __html: t.raw('content.managingCookies.items.1') }} />
                                <li dangerouslySetInnerHTML={{ __html: t.raw('content.managingCookies.items.2') }} />
                            </ul>
                            <div className={styles.infoBox}>
                                <p>{t('content.managingCookies.note')}</p>
                            </div>
                        </section>

                        <section id="changes" className={styles.contentSection}>
                            <h2 className={styles.sectionTitle}>{t('content.changes.title')}</h2>
                            <p className={styles.contentText}>{t('content.changes.text')}</p>
                        </section>

                        <section id="contact" className={styles.contentSection}>
                            <h2 className={styles.sectionTitle}>{t('content.contact.title')}</h2>
                            <p className={styles.contentText}>{t('content.contact.text')}</p>
                            <p className={styles.contentText}>
                                {t('content.contact.email')} <a href="mailto:support@aceb.com" style={{ color: 'var(--color-primary)', textDecoration: 'none' }}>support@aceb.com</a>
                            </p>
                        </section>

                        <div className={styles.lastUpdated}>{t('content.lastUpdated')}</div>
                    </div>

                    <div>
                        <div className={styles.tocSidebar}>
                            <h3 className={styles.tocTitle}>
                                <AssignmentIcon fontSize="small" />
                                {t('toc.title')}
                            </h3>

                            <nav aria-label="table of contents">
                                <ul className={styles.tocList}>
                                    {sections.map((section) => (
                                        <li
                                            key={section.id}
                                            className={`${styles.tocItem} ${activeSection === section.id ? styles.tocItemActive : ''}`}
                                            onClick={() => scrollToSection(section.id)}
                                        >
                                            {section.title}
                                        </li>
                                    ))}
                                </ul>
                            </nav>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
